"use client";

import { useMemo } from "react";
import { useTranslations } from "use-intl";

import { CalendarIcon } from "@/components/appIcons";
import { useFormatNumber } from "@/hooks/use-format-number";

import { FacetOptionGrid } from "./facet-option-list";
import { SEARCH_DECADES } from "./search-decades";

function parseYearInput(value: string) {
  const trimmedValue = value.trim();
  if (!trimmedValue) return undefined;
  const year = Number(trimmedValue);
  return Number.isFinite(year) && year > 0 ? year : undefined;
}

export function SearchYearFilters({
  yearFrom,
  yearTo,
  yearCounts,
  updateFilter,
  updateFilters,
}: {
  yearFrom: number | undefined;
  yearTo: number | undefined;
  yearCounts: { value: string; count: number }[];
  updateFilter: (key: string, value: string | number | boolean | undefined) => void;
  updateFilters: (updates: Record<string, string | number | undefined>) => void;
}) {
  const t = useTranslations("globals.search");
  const formatNumber = useFormatNumber();
  const decadeOptions = useMemo(() => {
    const yearMap = new Map(yearCounts.map((item) => [Number(item.value), item.count]));
    return SEARCH_DECADES.map((decade) => ({
      ...decade,
      count: Array.from(
        { length: decade.to - decade.from + 1 },
        (_, index) => yearMap.get(decade.from + index) ?? 0,
      ).reduce((sum, count) => sum + count, 0),
    }));
  }, [yearCounts]);
  const selectedDecadeValue =
    yearFrom != null && yearTo != null ? `${yearFrom}-${yearTo}` : undefined;
  const commitYearFilter = (
    key: "yearFrom" | "yearTo",
    value: string,
    currentYear: number | undefined,
  ) => {
    const nextYear = parseYearInput(value);
    if (nextYear !== currentYear) updateFilter(key, nextYear);
  };
  return (
    <>
      <hr className="border-border" />
      <div>
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <CalendarIcon className="text-sm" /> {t("year")}
        </h3>
        <div className="mb-3">
          <FacetOptionGrid
            items={decadeOptions.map((decade) => ({
              value: `${decade.from}-${decade.to}`,
              label: decade.label,
              count: decade.count,
              disabled: decade.count === 0,
            }))}
            value={selectedDecadeValue}
            onChange={(value) => {
              const decade = decadeOptions.find(
                (option) => `${option.from}-${option.to}` === value,
              );
              updateFilters({ yearFrom: decade?.from, yearTo: decade?.to });
            }}
            formatCount={formatNumber}
          />
        </div>
        <div className="flex items-center gap-2">
          {(["yearFrom", "yearTo"] as const).map((key) => {
            const current = key === "yearFrom" ? yearFrom : yearTo;
            return (
              <input
                key={`${key}-${current ?? ""}`}
                type="number"
                defaultValue={current ?? ""}
                placeholder={t(key === "yearFrom" ? "yearFromPlaceholder" : "yearToPlaceholder")}
                min={1900}
                max={2100}
                onBlur={(event) => commitYearFilter(key, event.target.value, current)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.currentTarget.blur();
                }}
                className="w-full max-sm:min-h-11 px-3 py-2 text-sm bg-secondary border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              />
            );
          })}
        </div>
      </div>
    </>
  );
}
